// Print utility for Khyati Portal.
// Builds a clean, branded, print-ready document in a popup window and triggers
// the browser print dialog — independent of the app's inline styles, so the
// printed output is always tidy regardless of the on-screen layout.

const RSR_LOGO = `
  <svg viewBox="0 0 220 50" width="150" height="34" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="9" width="19" height="19" fill="#E22D2D" transform="rotate(45,19,18)"/>
    <rect x="22" y="9" width="19" height="19" fill="#3B3A8F" transform="rotate(45,31,18)"/>
    <text x="56" y="31" font-family="Arial Black,sans-serif" font-size="29" font-weight="900" fill="#E22D2D">RSR</text>
    <rect x="56" y="34" width="92" height="13" rx="2" fill="#3B3A8F"/>
    <text x="102" y="44" font-family="Arial,sans-serif" font-size="7" font-weight="700" fill="white" text-anchor="middle" letter-spacing="1">INTERNATIONAL</text>
  </svg>`;

// Deroi Mart's mark. printDocument was RSR-only: the logo and the org line were
// hardcoded, so a Deroi document printed under RSR's letterhead and only the
// subtitle said otherwise. The `org` option below fixes that WITHOUT touching the
// 17 existing callers — omitting it still yields the RSR head, byte for byte.
const DEROI_LOGO = `
  <svg viewBox="0 0 220 50" width="150" height="34" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="9" width="19" height="19" fill="#166534" transform="rotate(45,19,18)"/>
    <rect x="22" y="9" width="19" height="19" fill="#0F3D22" transform="rotate(45,31,18)"/>
    <text x="56" y="31" font-family="Arial Black,sans-serif" font-size="25" font-weight="900" fill="#166534">DEROI</text>
    <rect x="56" y="34" width="112" height="13" rx="2" fill="#0F3D22"/>
    <text x="112" y="44" font-family="Arial,sans-serif" font-size="7" font-weight="700" fill="white" text-anchor="middle" letter-spacing="1">MART PVT LTD</text>
  </svg>`;

export const ORGS = {
  rsr:   { logo: RSR_LOGO,   line: 'RSR International · Khyati Portal', footer: 'RSR International' },
  deroi: { logo: DEROI_LOGO, line: 'Deroi Mart Pvt Ltd · Khyati Portal', footer: 'Deroi Mart Pvt Ltd' },
};

const esc = (v) => (v === null || v === undefined || v === '') ? '—'
  : String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// section = { title?: string, fields?: [[label, value], ...], table?: { columns:[], rows:[[]] } }
function renderSection(s) {
  let html = '';
  if (s.title) html += `<h3 class="sec">${esc(s.title)}</h3>`;
  if (s.fields && s.fields.length) {
    html += '<table class="kv">';
    for (const [k, v] of s.fields) {
      html += `<tr><td class="k">${esc(k)}</td><td class="v">${esc(v)}</td></tr>`;
    }
    html += '</table>';
  }
  if (s.table && s.table.rows) {
    const nCols = s.table.columns.length;
    // A row is normally an array of cells. It may INSTEAD be { note: '...' }, which renders as a
    // full-width continuation row under the row above it (mig 307). Two reasons it is a spanning
    // row and not a column: the real notes are multi-line ("Foma Interlock, 77% polyester…" then
    // a blank line then "PANTONE 19-1110 TPG"), which a column would either truncate or stretch;
    // and a line table already carrying Item/HSN/Qty/Unit/Rate/Taxable/GST/Tax/Total has no width
    // left to give. A footnote was the other option and is worse — it separates the instruction
    // from the quantity it qualifies, which is the whole point of the note.
    const renderRow = (r) => Array.isArray(r)
      ? '<tr>' + r.map(c => `<td>${esc(c)}</td>`).join('') + '</tr>'
      : (r && r.note != null && String(r.note).trim()
          ? `<tr class="noterow"><td colspan="${nCols}"><span class="notelbl">Note</span>${esc(String(r.note).trim())}</td></tr>`
          : '');
    html += '<table class="grid"><thead><tr>'
      + s.table.columns.map(c => `<th>${esc(c)}</th>`).join('')
      + '</tr></thead><tbody>'
      + s.table.rows.map(renderRow).join('')
      + '</tbody></table>';
  }
  return html;
}

/**
 * WHICH NOTE PRINTS AGAINST WHICH LINE (mig 307).
 *
 * Lines carry their own notes now. 171 purchase orders raised BEFORE that carry one on the
 * HEADER instead — and 169 of them have exactly one line, where "the order's note" and "the
 * line's note" are the same sentence and almost always an item specification ("2 PLY THREAD
 * NEEED", "SEMOLINA 15-1218 TPG"). So on a single-line order the header note is BORROWED into
 * the line's note position: the whole back catalogue keeps its note where a reader expects it
 * and not one row was rewritten to achieve it.
 *
 * It is NOT borrowed when the order has two or more lines. There the header note genuinely does
 * not say which line it means — "5 % extra allowed" on TPO-425260173-1 is the case — and
 * answering that question here would be an assertion nobody made. Those keep a separate Notes
 * block, exactly as they print today. A line with its own note always wins over a borrowed one.
 *
 * Returns { borrowed, noteOf(line, i) }. `borrowed` also tells the caller to drop its own Notes
 * section, or the vendor reads one instruction printed twice.
 */
export function lineNotes(lines = [], poNote = '') {
  const note = String(poNote || '').trim();
  const borrowed = lines.length === 1 && !!note && !String(lines[0] && lines[0].notes || '').trim();
  return {
    borrowed,
    noteOf: (l, i) => (borrowed && i === 0 ? note : String((l && l.notes) || '').trim()),
  };
}

/**
 * printDocument({ docTitle, subtitle, meta, sections, org })
 *  - docTitle: big heading, e.g. "Greige Purchase Order"
 *  - subtitle: e.g. "GPO No: 1806"
 *  - meta: [[label, value], ...] shown top-right (date, status, etc.)
 *  - sections: array of section objects (see renderSection)
 *  - org: 'rsr' (default) | 'deroi' — which letterhead to print under. Optional
 *    and defaulted, so every existing caller is unchanged.
 */
export function printDocument({ docTitle, subtitle = '', meta = [], sections = [], org = 'rsr' }) {
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) { alert('Please allow pop-ups to print.'); return; }
  const brand = ORGS[String(org).toLowerCase()] || ORGS.rsr;
  const printedOn = new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const metaHtml = meta.map(([k, v]) => `<div><span>${esc(k)}:</span> <b>${esc(v)}</b></div>`).join('');

  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(docTitle)} ${esc(subtitle)}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1a1a2e; margin: 28px; font-size: 12px; }
    .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #3B3A8F; padding-bottom: 12px; margin-bottom: 18px; }
    .head .meta { text-align: right; font-size: 11px; color: #555; }
    .head .meta div { margin-bottom: 2px; }
    .head .meta span { color: #999; }
    .org { font-size: 10px; color: #888; margin-top: 4px; }
    h1 { font-size: 20px; margin: 0 0 2px; color: #1a1a2e; }
    .subtitle { font-size: 13px; color: #3B3A8F; font-weight: 700; margin-bottom: 16px; }
    h3.sec { font-size: 13px; color: #3B3A8F; border-bottom: 1px solid #e0e0e0; padding-bottom: 4px; margin: 18px 0 8px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
    table.kv td { padding: 5px 8px; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
    table.kv td.k { color: #888; width: 35%; font-weight: 600; }
    table.kv td.v { color: #1a1a2e; }
    table.grid th { background: #f4f5fa; text-align: left; padding: 7px 8px; border: 1px solid #e0e0e0; font-size: 11px; color: #555; }
    table.grid td { padding: 6px 8px; border: 1px solid #eee; }
    /* The per-line note (mig 307). It reads as a continuation of the line above it, so the top
       border is removed and the text is indented and muted — it must not look like a second item.
       pre-line keeps the line breaks people actually type ("…polyester," ⏎ ⏎ "PANTONE 19-1110"). */
    table.grid tr.noterow td { border-top: none; padding: 2px 8px 7px 22px; color: #555;
      font-size: 11px; white-space: pre-line; background: #fcfcfd; }
    table.grid tr.noterow .notelbl { color: #999; font-weight: 700; text-transform: uppercase;
      font-size: 9px; letter-spacing: 0.5px; margin-right: 7px; }
    @media print { table.grid tr { break-inside: avoid; page-break-inside: avoid; } }
    .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e0e0e0; font-size: 10px; color: #aaa; text-align: center; }
    @media print { body { margin: 12px; } @page { margin: 14mm; } }
  </style></head><body>
    <div class="head">
      <div>${brand.logo}<div class="org">${esc(brand.line)}</div></div>
      <div class="meta"><div><span>Printed:</span> ${esc(printedOn)}</div>${metaHtml}</div>
    </div>
    <h1>${esc(docTitle)}</h1>
    ${subtitle ? `<div class="subtitle">${esc(subtitle)}</div>` : ''}
    ${sections.map(renderSection).join('')}
    <div class="footer">This is a system-generated document from Khyati Portal — ${esc(brand.footer)}.</div>
  </body></html>`);
  win.document.close();
  win.focus();
  // Give the browser a tick to lay out before invoking print.
  setTimeout(() => { win.print(); }, 350);
}

// Small reusable print button factory (returns props for a <button>).
export const printBtnStyle = {
  padding: '6px 14px', background: '#EEEDF8', color: '#3B3A8F', border: '1px solid #d8d6f0',
  borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', gap: 5,
};

export default printDocument;
